import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import type { WorkspaceSettings } from "@wedding/schemas";
import { Button, Card, Skeleton } from "@wedding/ui";
import {
  ArchiveRestore,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { api, apiFetch } from "../api";

type CollaboratorRole = "owner" | "admin" | "editor" | "viewer";
type Collaborator = {
  id: string;
  email: string;
  role: CollaboratorRole;
  addedAt: string;
};
type CollaboratorResponse = {
  members: Collaborator[];
  currentRole: CollaboratorRole;
};
type SettingsForm = WorkspaceSettings & {
  budgetLimitYuan: number;
  emergencyFundPercent: number;
};
type BackupResult = {
  id: string;
  generatedAt: string;
  recordCount: number;
};

const roleLabels: Record<CollaboratorRole, string> = {
  owner: "所有者",
  admin: "管理员",
  editor: "可编辑",
  viewer: "仅查看",
};

export function SettingsPage() {
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId: string;
  };
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [collaboratorEmail, setCollaboratorEmail] = useState("");
  const [collaboratorRole, setCollaboratorRole] = useState<"editor" | "viewer">(
    "editor",
  );
  const [pendingSettings, setPendingSettings] = useState<WorkspaceSettings>();
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirmation, setResetConfirmation] = useState("");
  const [resetResult, setResetResult] = useState("");
  const [removingMember, setRemovingMember] = useState<Collaborator>();
  const [restoringBackup, setRestoringBackup] = useState<BackupResult>();
  const workspace = useQuery({
    queryKey: [workspaceId, "workspace"],
    queryFn: () => api.workspace(workspaceId).then((result) => result.data),
  });
  const collaborators = useQuery({
    queryKey: [workspaceId, "members"],
    queryFn: () =>
      apiFetch<CollaboratorResponse>(`/workspaces/${workspaceId}/members`),
  });
  const canManage = ["owner", "admin"].includes(
    collaborators.data?.currentRole ?? "viewer",
  );
  const backups = useQuery({
    queryKey: [workspaceId, "backups"],
    queryFn: () =>
      apiFetch<BackupResult[]>(`/workspaces/${workspaceId}/backups`),
    enabled: canManage,
  });
  const { register, handleSubmit } = useForm<SettingsForm>();

  const saveCollaborator = useMutation({
    mutationFn: ({
      email,
      role,
    }: {
      email: string;
      role: "editor" | "viewer";
    }) =>
      apiFetch<Collaborator>(`/workspaces/${workspaceId}/members`, {
        method: "POST",
        body: JSON.stringify({ email, role }),
      }),
    onSuccess: async () => {
      setCollaboratorEmail("");
      await queryClient.invalidateQueries({
        queryKey: [workspaceId, "members"],
      });
    },
  });
  const removeCollaborator = useMutation({
    mutationFn: (member: Collaborator) =>
      apiFetch(`/workspaces/${workspaceId}/members/${member.id}`, {
        method: "DELETE",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [workspaceId, "members"],
      });
      setRemovingMember(undefined);
    },
  });

  const save = useMutation({
    mutationFn: ({
      settings,
      confirmDateChange,
    }: {
      settings: WorkspaceSettings;
      confirmDateChange: boolean;
    }) =>
      apiFetch(`/workspaces/${workspaceId}`, {
        method: "PATCH",
        body: JSON.stringify({
          settings,
          expectedVersion: workspace.data?.version,
          confirmDateChange,
        }),
      }),
    onSuccess: async () => {
      setPendingSettings(undefined);
      await workspace.refetch();
    },
  });

  const reset = useMutation({
    mutationFn: async () => {
      const backup = await apiFetch<BackupResult>(
        `/workspaces/${workspaceId}/backups`,
        {
          method: "POST",
          body: "{}",
        },
      );
      await apiFetch(`/workspaces/${workspaceId}/import/seed`, {
        method: "POST",
        body: JSON.stringify({ reset: true, confirmation: "RESET_TO_SEED" }),
      });
      return backup;
    },
    onSuccess: async () => {
      setResetOpen(false);
      setResetConfirmation("");
      setResetResult("重置完成。重置前的完整备份已经保留，可在需要时恢复。");
      await queryClient.invalidateQueries({
        queryKey: [workspaceId, "backups"],
      });
      await queryClient.invalidateQueries({ queryKey: [workspaceId] });
      await navigate({
        to: "/w/$workspaceId/dashboard",
        params: { workspaceId },
      });
    },
  });
  const createBackup = useMutation({
    mutationFn: () =>
      apiFetch<BackupResult>(`/workspaces/${workspaceId}/backups`, {
        method: "POST",
        body: "{}",
      }),
    onSuccess: async () => {
      setResetResult("完整备份已创建。");
      await queryClient.invalidateQueries({
        queryKey: [workspaceId, "backups"],
      });
    },
  });
  const restore = useMutation({
    mutationFn: (backup: BackupResult) =>
      apiFetch(`/workspaces/${workspaceId}/backups/${backup.id}/restore`, {
        method: "POST",
        body: JSON.stringify({ dryRun: false }),
      }),
    onSuccess: async () => {
      setRestoringBackup(undefined);
      setResetResult("备份已恢复；恢复前的当前状态也已自动备份。");
      await queryClient.invalidateQueries({ queryKey: [workspaceId] });
    },
  });

  const submitSettings = (form: SettingsForm) => {
    const { budgetLimitYuan, emergencyFundPercent, ...rest } = form;
    const settings: WorkspaceSettings = {
      ...rest,
      budgetLimitCents: Math.round((budgetLimitYuan || 0) * 100),
      emergencyFundRate: (emergencyFundPercent || 0) / 100,
    };
    if (settings.weddingDate !== workspace.data?.settings.weddingDate) {
      setPendingSettings(settings);
      return;
    }
    save.mutate({ settings, confirmDateChange: false });
  };

  if (workspace.isLoading)
    return (
      <div className="page">
        <Skeleton />
      </div>
    );
  const current = workspace.data!;

  return (
    <div className="page settings-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">项目管理</span>
          <h1>项目设置</h1>
          <p>修改婚礼日期会重新计算自动模式任务，手动日期保持不变。</p>
        </div>
      </header>

      <Card>
        <form key={current.version} onSubmit={handleSubmit(submitSettings)}>
          {!canManage && (
            <div className="settings-readonly-notice">
              你是该项目的协作者，可以编辑筹备数据，但只有所有者可修改项目设置和成员。
            </div>
          )}
          <fieldset className="settings-fields" disabled={!canManage}>
            <div className="form-grid">
              <label>
                <span>项目名称</span>
                <input
                  defaultValue={current.settings.projectName}
                  {...register("projectName")}
                />
              </label>
              <label>
                <span>婚礼日期</span>
                <input
                  type="date"
                  defaultValue={current.settings.weddingDate}
                  {...register("weddingDate")}
                />
              </label>
              <label>
                <span>新娘姓名</span>
                <input
                  defaultValue={current.settings.brideName}
                  {...register("brideName")}
                />
              </label>
              <label>
                <span>新郎姓名</span>
                <input
                  defaultValue={current.settings.groomName}
                  {...register("groomName")}
                />
              </label>
              <label>
                <span>婚礼城市</span>
                <input
                  defaultValue={current.settings.city}
                  {...register("city")}
                />
              </label>
              <label>
                <span>仪式形式</span>
                <input
                  defaultValue={current.settings.ceremonyStyle}
                  {...register("ceremonyStyle")}
                />
              </label>
              <label>
                <span>宾客目标</span>
                <input
                  type="number"
                  defaultValue={current.settings.guestTarget}
                  {...register("guestTarget", { valueAsNumber: true })}
                />
              </label>
              <label>
                <span>桌数目标</span>
                <input
                  type="number"
                  min="0"
                  defaultValue={current.settings.tableCount}
                  {...register("tableCount", { valueAsNumber: true })}
                />
              </label>
              <label>
                <span>每桌容量</span>
                <input
                  type="number"
                  defaultValue={current.settings.tableCapacity}
                  {...register("tableCapacity", { valueAsNumber: true })}
                />
              </label>
              <label>
                <span>总预算（元）</span>
                <input
                  type="number"
                  min="0"
                  step="100"
                  defaultValue={current.settings.budgetLimitCents / 100}
                  {...register("budgetLimitYuan", { valueAsNumber: true })}
                />
              </label>
              <label>
                <span>应急预算比例（%）</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  defaultValue={current.settings.emergencyFundRate * 100}
                  {...register("emergencyFundPercent", { valueAsNumber: true })}
                />
              </label>
              <label>
                <span>筹备状态</span>
                <select
                  defaultValue={current.settings.status}
                  {...register("status")}
                >
                  <option>未开始</option>
                  <option>筹备中</option>
                  <option>已完成</option>
                  <option>已归档</option>
                </select>
              </label>
              <label>
                <span>时区</span>
                <select
                  defaultValue={current.settings.timezone}
                  {...register("timezone")}
                >
                  <option value="Asia/Shanghai">中国标准时间</option>
                  <option value="Europe/London">英国时间</option>
                  <option value="America/New_York">美国东部时间</option>
                  <option value="America/Los_Angeles">美国西部时间</option>
                </select>
              </label>
              <label>
                <span>任务缓冲天数</span>
                <input
                  type="number"
                  min="0"
                  defaultValue={current.settings.bufferDays}
                  {...register("bufferDays", { valueAsNumber: true })}
                />
              </label>
              <label>
                <span>回执截止日期</span>
                <input
                  type="date"
                  defaultValue={current.settings.rsvpDeadline ?? ""}
                  {...register("rsvpDeadline", {
                    setValueAs: (value) => value || undefined,
                  })}
                />
              </label>
              <label>
                <span>主品牌色</span>
                <input
                  type="color"
                  defaultValue={current.settings.primaryColor}
                  {...register("primaryColor")}
                />
              </label>
            </div>
            <input
              type="hidden"
              value={current.settings.preparationStartDate}
              {...register("preparationStartDate")}
            />
            <input
              type="hidden"
              value={current.settings.currency}
              {...register("currency")}
            />
            <input
              type="hidden"
              value={current.settings.region}
              {...register("region")}
            />
            <input
              type="hidden"
              value={current.settings.monogram}
              {...register("monogram")}
            />
            <input
              type="hidden"
              value={JSON.stringify(current.settings.secondaryColors)}
              {...register("secondaryColors", {
                setValueAs: (value) =>
                  typeof value === "string" ? JSON.parse(value) : value,
              })}
            />
            {canManage && (
              <footer>
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? "保存中…" : "保存设置"}
                </Button>
              </footer>
            )}
          </fieldset>
        </form>
      </Card>

      <Card className="collaborator-card">
        <header>
          <div className="collaborator-heading">
            <Users size={20} />
            <div>
              <span className="eyebrow">共同筹备</span>
              <h2>项目协作者</h2>
            </div>
          </div>
          <p>
            用邮箱添加一起筹备婚礼的人。对方用这个邮箱登录后，就能在项目列表中看到本项目；若无法登录，请联系系统管理员开通该邮箱。
          </p>
        </header>
        {canManage && (
          <form
            className="collaborator-form"
            onSubmit={(event) => {
              event.preventDefault();
              saveCollaborator.mutate({
                email: collaboratorEmail,
                role: collaboratorRole,
              });
            }}
          >
            <label>
              <span>协作者邮箱</span>
              <input
                required
                type="email"
                value={collaboratorEmail}
                onChange={(event) => setCollaboratorEmail(event.target.value)}
                placeholder="partner@example.com"
              />
            </label>
            <label>
              <span>权限</span>
              <select
                value={collaboratorRole}
                onChange={(event) =>
                  setCollaboratorRole(event.target.value as "editor" | "viewer")
                }
              >
                <option value="editor">可编辑</option>
                <option value="viewer">仅查看</option>
              </select>
            </label>
            <Button type="submit" disabled={saveCollaborator.isPending}>
              <UserPlus size={16} />
              {saveCollaborator.isPending ? "添加中…" : "添加协作者"}
            </Button>
          </form>
        )}
        {saveCollaborator.error && (
          <p className="form-error">{saveCollaborator.error.message}</p>
        )}
        {collaborators.isLoading ? (
          <Skeleton lines={3} />
        ) : collaborators.isError ? (
          <p className="form-error">{collaborators.error.message}</p>
        ) : (
          <div className="collaborator-list">
            {collaborators.data?.members.map((member) => (
              <div className="collaborator-row" key={member.id}>
                <div className="collaborator-avatar">
                  {member.email.slice(0, 1).toUpperCase()}
                </div>
                <div>
                  <strong>{member.email}</strong>
                  <span>{roleLabels[member.role]}</span>
                </div>
                {canManage && member.role !== "owner" && (
                  <>
                    <select
                      aria-label={`修改 ${member.email} 的权限`}
                      value={member.role}
                      disabled={saveCollaborator.isPending}
                      onChange={(event) =>
                        saveCollaborator.mutate({
                          email: member.email,
                          role: event.target.value as "editor" | "viewer",
                        })
                      }
                    >
                      <option value="editor">可编辑</option>
                      <option value="viewer">仅查看</option>
                    </select>
                    <button
                      type="button"
                      className="member-remove"
                      aria-label={`移除协作者 ${member.email}`}
                      disabled={removeCollaborator.isPending}
                      onClick={() => setRemovingMember(member)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            ))}
            {!collaborators.data?.members.length && (
              <p className="collaborator-empty">还没有协作者。</p>
            )}
          </div>
        )}
        {removeCollaborator.error && (
          <p className="form-error">{removeCollaborator.error.message}</p>
        )}
      </Card>

      {canManage && (
        <Card className="backup-card">
          <header>
            <div>
              <span className="eyebrow">数据保护</span>
              <h2>备份与恢复</h2>
              <p>手动保存一个完整快照；恢复前也会先备份当前状态。</p>
            </div>
            <Button
              className="secondary"
              onClick={() => createBackup.mutate()}
              disabled={createBackup.isPending}
            >
              {createBackup.isPending ? "备份中…" : "立即备份"}
            </Button>
          </header>
          {createBackup.error && (
            <p className="form-error">{createBackup.error.message}</p>
          )}
          {backups.isLoading ? (
            <Skeleton lines={3} />
          ) : (
            <div className="backup-list">
              {backups.data?.slice(0, 6).map((backup) => (
                <div key={backup.id}>
                  <div>
                    <strong>
                      {new Date(backup.generatedAt).toLocaleString("zh-CN")}
                    </strong>
                    <span>{backup.recordCount} 条记录</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRestoringBackup(backup)}
                  >
                    <ArchiveRestore size={15} /> 恢复
                  </button>
                </div>
              ))}
              {!backups.data?.length && <p>还没有备份。</p>}
            </div>
          )}
        </Card>
      )}

      {canManage && (
        <Card className="danger-zone">
          <div>
            <span className="eyebrow">谨慎操作</span>
            <h2>从头开始</h2>
            <p>
              清空当前筹备记录。操作前系统会自动创建一份完整备份，避免误操作后无法找回。
            </p>
            {resetResult && (
              <p className="settings-success">
                <ShieldCheck size={16} />
                {resetResult}
              </p>
            )}
            {reset.error && <p className="form-error">{reset.error.message}</p>}
          </div>
          <Button
            className="danger"
            onClick={() => setResetOpen(true)}
            disabled={reset.isPending}
          >
            清空并重新开始
          </Button>
        </Card>
      )}

      {pendingSettings && (
        <div className="drawer-backdrop">
          <aside
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label="确认修改婚礼日期"
          >
            <header>
              <div>
                <span className="eyebrow">确认日期变更</span>
                <h2>修改婚礼日期？</h2>
              </div>
              <button
                className="icon-button"
                aria-label="关闭"
                onClick={() => setPendingSettings(undefined)}
              >
                <X />
              </button>
            </header>
            <div className="confirmation-content">
              <p>
                婚礼日期将从 <strong>{current.settings.weddingDate}</strong>{" "}
                改为 <strong>{pendingSettings.weddingDate}</strong>。
              </p>
              <p>
                系统会重新计算自动安排的任务日期；你手动调整过的日期不会改变。
              </p>
            </div>
            <footer>
              <Button
                className="secondary"
                onClick={() => setPendingSettings(undefined)}
              >
                返回检查
              </Button>
              <Button
                onClick={() =>
                  save.mutate({
                    settings: pendingSettings,
                    confirmDateChange: true,
                  })
                }
                disabled={save.isPending}
              >
                {save.isPending ? "保存中…" : "确认并保存"}
              </Button>
            </footer>
          </aside>
        </div>
      )}

      {removingMember && (
        <div className="drawer-backdrop">
          <aside
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label="移除协作者"
          >
            <header>
              <div>
                <span className="eyebrow">协作者权限</span>
                <h2>移除协作者？</h2>
              </div>
              <button
                className="icon-button"
                aria-label="关闭"
                onClick={() => setRemovingMember(undefined)}
              >
                <X />
              </button>
            </header>
            <div className="confirmation-content">
              <p>
                移除后，<strong>{removingMember.email}</strong>{" "}
                将无法再查看或编辑这个婚礼项目。
              </p>
            </div>
            <footer>
              <Button
                className="secondary"
                onClick={() => setRemovingMember(undefined)}
              >
                取消
              </Button>
              <Button
                className="danger"
                onClick={() => removeCollaborator.mutate(removingMember)}
                disabled={removeCollaborator.isPending}
              >
                {removeCollaborator.isPending ? "移除中…" : "确认移除"}
              </Button>
            </footer>
          </aside>
        </div>
      )}

      {resetOpen && (
        <div className="drawer-backdrop">
          <aside
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label="清空项目"
          >
            <header>
              <div>
                <span className="eyebrow">高风险操作</span>
                <h2>清空并重新开始</h2>
              </div>
              <button
                className="icon-button"
                aria-label="关闭"
                onClick={() => setResetOpen(false)}
              >
                <X />
              </button>
            </header>
            <div className="confirmation-content">
              <div className="delete-warning">
                <ArchiveRestore />
                <div>
                  <strong>系统会先自动备份</strong>
                  <p>
                    备份成功后才会清空。默认示例项目会重新载入基线数据，其他项目会保留项目设置并清空筹备记录。
                  </p>
                </div>
              </div>
              <label className="delete-confirm-field">
                <span>
                  请输入项目名称「{current.settings.projectName}」确认
                </span>
                <input
                  autoFocus
                  value={resetConfirmation}
                  onChange={(event) => setResetConfirmation(event.target.value)}
                />
              </label>
              {reset.error && (
                <p className="form-error">{reset.error.message}</p>
              )}
            </div>
            <footer>
              <Button className="secondary" onClick={() => setResetOpen(false)}>
                取消
              </Button>
              <Button
                className="danger"
                onClick={() => reset.mutate()}
                disabled={
                  resetConfirmation !== current.settings.projectName ||
                  reset.isPending
                }
              >
                {reset.isPending ? "正在备份并重置…" : "备份后清空"}
              </Button>
            </footer>
          </aside>
        </div>
      )}
      {restoringBackup && (
        <div className="drawer-backdrop">
          <aside
            className="drawer"
            role="dialog"
            aria-modal="true"
            aria-label="恢复备份"
          >
            <header>
              <div>
                <span className="eyebrow">数据恢复</span>
                <h2>恢复这份备份？</h2>
              </div>
              <button
                className="icon-button"
                aria-label="关闭"
                onClick={() => setRestoringBackup(undefined)}
              >
                <X />
              </button>
            </header>
            <div className="confirmation-content">
              <p>
                项目将严格恢复到{" "}
                <strong>
                  {new Date(restoringBackup.generatedAt).toLocaleString(
                    "zh-CN",
                  )}
                </strong>{" "}
                的业务数据状态，共 {restoringBackup.recordCount}{" "}
                条记录；此后新增的业务记录也会移除。
              </p>
              <p>服务端会先校验备份完整性，并自动保存当前状态作为安全备份。</p>
              {restore.error && (
                <p className="form-error">{restore.error.message}</p>
              )}
            </div>
            <footer>
              <Button
                className="secondary"
                onClick={() => setRestoringBackup(undefined)}
              >
                取消
              </Button>
              <Button
                onClick={() => restore.mutate(restoringBackup)}
                disabled={restore.isPending}
              >
                {restore.isPending ? "校验并恢复中…" : "确认恢复"}
              </Button>
            </footer>
          </aside>
        </div>
      )}
    </div>
  );
}
